// Phase 85.A.49 — Google Identity Services (GIS) — thay thế Emergent bridge.
// Phase 85.A.50 — Native bridge (WebView Android → expo-auth-session).
// Web: dùng popup GIS chính chủ Google → BE verify id_token offline.
// Mobile native (Expo WebView): postMessage GOOGLE_SIGN_IN → native flow.
// Migration: user cũ đã tạo qua Emergent bridge match theo email → KHÔNG mất data.
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { GoogleLogo } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

const GIS_SCRIPT_SRC = "https://accounts.google.com/gsi/client";

function loadGisScript() {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }
    const existing = document.querySelector(`script[src="${GIS_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("GIS script load failed")));
      return;
    }
    const s = document.createElement("script");
    s.src = GIS_SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("GIS script load failed"));
    document.head.appendChild(s);
  });
}

export default function GoogleSignInButton({ label = "Tiếp tục với Google" }) {
  const [isNativeApp, setIsNativeApp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const mountedRef = useRef(true);
  const clientId = process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID;

  useEffect(() => () => { mountedRef.current = false; }, []);

  useEffect(() => {
    const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
    const flag = typeof window !== "undefined" && window.__pickleagueNativeApp === true;
    const inWebView = typeof window !== "undefined" && !!window.ReactNativeWebView;
    if (flag || inWebView || /PickLeague\/[\d.]+\s+Mobile/i.test(ua)) {
      setIsNativeApp(true);
    }
  }, []);

  const handleCredential = useCallback(async (credential) => {
    setLoading(true);
    try {
      await api.post("/auth/google/verify-id-token", { id_token: credential, platform: "web" });
      await refreshMe();
      toast.success("Đăng nhập Google thành công!");
      navigate("/", { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Đăng nhập Google thất bại.";
      toast.error(msg);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [refreshMe, navigate]);

  // Phase 85.A.50 — Native bridge handler (WebView Android).
  const handleNativeSignIn = useCallback(() => {
    if (!window.ReactNativeWebView) {
      toast.error("Native bridge không khả dụng.");
      return;
    }
    setLoading(true);
    const onMsg = async (e) => {
      const detail = e?.detail || {};
      window.removeEventListener("native-google-signin", onMsg);
      if (detail.cancelled) {
        if (mountedRef.current) setLoading(false);
        return;
      }
      if (detail.error) {
        if (mountedRef.current) setLoading(false);
        toast.error("Đăng nhập Google thất bại: " + detail.error);
        return;
      }
      if (!detail.id_token) {
        if (mountedRef.current) setLoading(false);
        toast.error("Không nhận được token từ Google.");
        return;
      }
      try {
        await api.post("/auth/google/verify-id-token", { id_token: detail.id_token, platform: "android" });
        await refreshMe();
        toast.success("Đăng nhập Google thành công!");
        navigate("/", { replace: true });
      } catch (err) {
        const dt = err?.response?.data?.detail;
        toast.error(typeof dt === "string" ? dt : dt?.message || "Đăng nhập Google thất bại.");
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    };
    window.addEventListener("native-google-signin", onMsg);
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: "GOOGLE_SIGN_IN" }));
    // Safety timeout: 60s.
    setTimeout(() => {
      window.removeEventListener("native-google-signin", onMsg);
      if (mountedRef.current) setLoading(false);
    }, 60000);
  }, [refreshMe, navigate]);

  // Web fallback: OAuth2 popup khi One Tap bị block 3rd party cookie.
  const openOAuthPopup = useCallback(() => {
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: "openid email profile",
      callback: async (response) => {
        if (response?.access_token) {
          try {
            setLoading(true);
            await api.post("/auth/google/verify-access-token", { access_token: response.access_token, platform: "web" });
            await refreshMe();
            toast.success("Đăng nhập Google thành công!");
            navigate("/", { replace: true });
          } catch (e) {
            const dt = e?.response?.data?.detail;
            toast.error(typeof dt === "string" ? dt : dt?.message || "Đăng nhập Google thất bại.");
          } finally {
            if (mountedRef.current) setLoading(false);
          }
        }
      },
    });
    client.requestAccessToken();
  }, [clientId, refreshMe, navigate]);

  // Init GIS — chỉ web, ko init trên native.
  useEffect(() => {
    if (isNativeApp || !clientId) return;
    let cancelled = false;
    (async () => {
      try {
        await loadGisScript();
        if (cancelled) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (resp) => {
            if (resp?.credential) handleCredential(resp.credential);
          },
          auto_select: false,
          ux_mode: "popup",
          itp_support: true,
        });
        if (!cancelled) setReady(true);
      } catch (e) {
        console.warn("[GIS] init failed:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [isNativeApp, clientId, handleCredential]);

  // ─── RENDER ───────────────────────────────────────────────────────

  if (isNativeApp) {
    // Phase 85.A.50 — Native bridge button (WebView Android).
    return (
      <button
        type="button"
        onClick={handleNativeSignIn}
        disabled={loading}
        className="w-full inline-flex items-center justify-center gap-3 px-6 py-3.5 rounded-[18px] bg-white text-[#060B14] font-black text-[14px] active:scale-[0.98] transition hover:bg-white/95 shadow-[0_4px_18px_rgba(255,255,255,0.18)] disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="google-signin-btn-native"
        title="Đăng nhập Google qua tài khoản trên thiết bị (bypass WebView Google block)."
      >
        <GoogleLogo size={18} weight="bold" className="text-[#EA4335]" />
        {loading ? "Đang đăng nhập…" : label}
      </button>
    );
  }

  if (!clientId) {
    if (typeof window !== "undefined") {
      console.warn("[GoogleSignInButton] REACT_APP_GOOGLE_WEB_CLIENT_ID missing.");
    }
    return null;
  }

  const handleClick = () => {
    if (!ready || !window.google?.accounts?.id) {
      toast.error("Đang khởi tạo Google… thử lại sau 1 giây.");
      return;
    }
    try {
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed?.() || notification.isSkippedMoment?.()) {
          openOAuthPopup();
        }
      });
    } catch (e) {
      console.warn("[GIS] prompt failed, fallback OAuth2:", e);
      openOAuthPopup();
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading || !ready}
      className="w-full inline-flex items-center justify-center gap-3 px-6 py-3.5 rounded-[18px] bg-white text-[#060B14] font-black text-[14px] active:scale-[0.98] transition hover:bg-white/95 shadow-[0_4px_18px_rgba(255,255,255,0.18)] disabled:opacity-60 disabled:cursor-not-allowed"
      data-testid="google-signin-btn"
      title="Đăng nhập bằng Google qua Google Identity Services (GIS) — không cần redirect, an toàn, không tốn cost."
    >
      <GoogleLogo size={18} weight="bold" className="text-[#EA4335]" />
      {loading ? "Đang đăng nhập…" : label}
    </button>
  );
}
